import React, { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import styled from "styled-components";
import LandingConnector from "./Connector/LandingConnector";
import Loader from "../Generic/Loader";
import xook from "../Assets/Xook.png";
import Button from "../Generic/Button";
import { useRBAC } from "../context/rbac/RBACContext";
import { DashboardModules } from "../config/app.config";
import useMachines from "../util/hooks/useMachines";

const Page = styled.div`
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 2rem 1rem;
	overflow-x: hidden;
	overflow-y: auto;
`;

const Column = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	max-width: 28rem;
`;

const Wordmark = styled.div`
	margin-top: 1rem;
	font-size: 1.4rem;
	color: rgb(45, 35, 66);
	letter-spacing: 0.05em;
`;

const RestaurantList = styled.div`
	width: 100%;
	margin-top: 2rem;
	max-height: calc(100vh - 18rem);
	overflow-y: auto;
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
`;

const RestaurantRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	min-width: 0;
	background: #fff;
	box-shadow: 0 1px 3px rgba(45, 35, 66, 0.08);
	border-radius: 14px;
	padding: 1rem 1.25rem;
	cursor: pointer;
	transition: background 0.15s ease-in-out;
	&:hover {
		background: #eceaf0;
	}
`;

const RestaurantInfo = styled.div`
	display: flex;
	flex-direction: column;
	min-width: 0;
	gap: 0.2rem;
`;

const RestaurantName = styled.div`
	color: rgba(45, 35, 66, 0.55);
	font-size: 0.8rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const RestaurantId = styled.div`
	color: #000;
	font-size: 1rem;
	font-weight: 600;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Chevron = styled.span`
	display: flex;
	flex-shrink: 0;
	margin-left: 0.75rem;
	color: rgba(45, 35, 66, 0.4);
`;

const ModuleLinksRow = styled.div`
	margin-top: 1.5rem;
	color: rgba(45, 35, 66, 0.55);
	font-size: 0.9rem;
`;

const ModuleLink = styled(NavLink)`
	color: inherit;
	text-decoration: none;
	&:hover {
		color: rgb(45, 35, 66);
	}
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 1rem;
	margin-top: 2rem;
	color: rgba(45, 35, 66, 0.7);
`;

const LoaderWrapper = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 2rem;
`;

const ChevronIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round">
		<polyline points="9 18 15 12 9 6" />
	</svg>
);

const MachineSelect = (props) => {
	let navigate = useNavigate();
	const { machines, loading, reload } = useMachines();
	const { canAccessModule } = useRBAC();
	const canAccessStudio = canAccessModule(DashboardModules.STUDIO);
	const canAccessAdmin = canAccessModule(DashboardModules.ADMIN);
	const canAccessOffers = canAccessModule(DashboardModules.OFFERS);

	function selectMachine(id) {
		const machineData = machines.find((m) => m._id === id);
		if (machineData) {
			props.setMachineID(machineData);
			navigate(`/${id}`);
		}
	}

	function clearAllTimeouts() {
		props.setMachineID({ _id: "", name: "", kitchenId: "" });
		var id = window.setTimeout(function () {}, 0);

		while (id--) {
			window.clearTimeout(id); // will do nothing if no timeout with id is present
		}
	}

	useEffect(() => {
		clearAllTimeouts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Page>
			<Column>
				<img src={xook} alt="logo" style={{ height: "3.5rem" }} />
				<Wordmark>RESTO FROM THE FUTURE</Wordmark>

				{loading && (
					<LoaderWrapper>
						<Loader />
					</LoaderWrapper>
				)}

				{!loading && machines.length === 0 && (
					<EmptyState>
						<div>No Machines Found</div>
						<Button active onClick={reload}>Retry</Button>
					</EmptyState>
				)}

				{!loading && machines.length > 0 && (
					<RestaurantList>
						{machines.map((machine) => (
							<RestaurantRow key={machine._id} onClick={() => selectMachine(machine._id)}>
								<RestaurantInfo>
									<RestaurantId>{machine._id}</RestaurantId>
									<RestaurantName>{machine.name}</RestaurantName>
								</RestaurantInfo>
								<Chevron><ChevronIcon /></Chevron>
							</RestaurantRow>
						))}
					</RestaurantList>
				)}

				{/* TODO: Fix this. */}

				{(canAccessStudio || canAccessAdmin || canAccessOffers) && (
					<ModuleLinksRow>
						{canAccessStudio && <ModuleLink to="/studio">Studio</ModuleLink>}
						{canAccessStudio && canAccessAdmin && " · "}
						{canAccessAdmin && <ModuleLink to="/admin">Admin</ModuleLink>}
						{(canAccessStudio || canAccessAdmin) && canAccessOffers && " · "}
						{canAccessOffers && <ModuleLink to="/offers">Offers</ModuleLink>}
					</ModuleLinksRow>
				)}
			</Column>
		</Page>
	);
};

export default LandingConnector(MachineSelect);
